import { useEffect, useRef } from "react";
import { Quote } from "lucide-react";
import { useLang } from "../LanguageContext";

const BG_IMAGE = "https://customer-assets.emergentagent.com/job_36f9bff3-a4eb-4daf-b32b-18b45c82cb5e/artifacts/maodrx1j_TorreAmat_Restaurant_-2.jpg";

export default function TestimoniosSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="testimonios"
      ref={ref}
      data-testid="testimonios-section"
      className="relative py-24 md:py-36 overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-[#1A1A18]/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 text-center">
          <p
            className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.testimonios.caption}
          </p>
          <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-8 mx-auto" />
          <h2
            className="fade-in-item text-4xl md:text-5xl font-light text-[#F5F0E8] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t.testimonios.heading}
          </h2>
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {t.testimonios.items.map((item, i) => (
            <div
              key={i}
              data-testid={`testimonio-card-${i}`}
              className="fade-in-item bg-[#F5F0E8]/8 backdrop-blur-sm border border-[#C8A96E]/20 p-8 md:p-10 hover:border-[#C8A96E]/50 transition-all duration-400 group"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <Quote size={24} className="text-[#C8A96E] mb-6 opacity-60" strokeWidth={1.5} />
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, s) => (
                  <div key={s} className="w-2 h-2 bg-[#C8A96E] rounded-full" />
                ))}
              </div>
              <p
                className="text-base md:text-lg text-[#F5F0E8]/85 leading-relaxed mb-8 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                "{item.text}"
              </p>
              <div className="border-t border-[#C8A96E]/20 pt-6">
                <p className="text-sm font-medium text-[#F5F0E8]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  {item.author}
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C8A96E] mt-1" style={{ fontFamily: "Lato, sans-serif" }}>
                  {item.role}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Leave a review CTA */}
        <div className="fade-in-item flex justify-center">
          <a
            data-testid="google-review-btn"
            href="https://www.google.com/maps/place/Torre+Amat.+Sarri%C3%A0/@41.3975213,2.1180833,17z/data=!4m8!3m7!1s0x12a498414dabd4cd:0xb4c919e11768725e!8m2!3d41.3975173!4d2.1206582!9m1!1b1!16s%2Fg%2F1thlq309?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 border border-[#C8A96E]/50 text-[#F5F0E8] text-xs uppercase tracking-[0.25em] hover:bg-[#C8A96E] hover:text-[#1A1A18] hover:border-[#C8A96E] transition-all duration-300"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="opacity-80">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
            </svg>
            Dejar una reseña en Google
          </a>
        </div>
      </div>
    </section>
  );
}
